import { Injectable } from '@angular/core';

import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject } from 'rxjs';

import { LogoutTimerService } from '.';
import { DecodedToken } from '../models';

@Injectable({
    providedIn: 'root'
})
export class TokenService {
    private _currentToken = '';

    token: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    get currentToken(): string {
        return this._currentToken;
    }

    constructor(
        private _logoutTimerService: LogoutTimerService
    ) {
        const sessionToken = sessionStorage.getItem("token");

        if (sessionToken) {
            if (this.isTokenValidAndNotExpired(sessionToken)) {
                this.setToken(sessionToken);
            }
            else {
                sessionStorage.removeItem('token');
            }
        }
    }

    private isTokenValidAndNotExpired(token: string): boolean {
        if (!token || token === '') {
            return false;
        }

        try {
            const decodedToken = jwtDecode<DecodedToken>(token);
            const currentTimeInSeconds = Date.now() / 1000;
            return decodedToken.exp > currentTimeInSeconds;
        }
        catch {
            return false;
        }
    }

    setToken(token: string): void {
        if (token === null || token === undefined) {
            this._currentToken = '';
            this.token.next(null);
            sessionStorage.removeItem('token');
            return;
        }

        this._currentToken = token;
        this.token.next(token);
        sessionStorage.setItem('token', token);
        this._logoutTimerService.resetTimer();
    }

    tokenIsStillValid(thresholdSeconds = 60): boolean {
        const token = sessionStorage.getItem('token');

        if (!token) {
            return false;
        }

        try {
            const decodedToken = jwtDecode<DecodedToken>(token);
            const currentTimeInSeconds = Date.now() / 1000;
            const expirationTime = decodedToken.exp - thresholdSeconds;

            if (expirationTime <= currentTimeInSeconds) {
                return false;
            }

            return true;
        }
        catch {
            return false;
        }
    }

    getSecondsUntilExpiration(): number | null {
        const token = sessionStorage.getItem('token');

        if ((token === null) || (token === undefined)) {
            return null;
        }

        try {
            const decodedToken = jwtDecode<DecodedToken>(token);
            const currentTimeInSeconds = Date.now() / 1000;
            return decodedToken.exp - currentTimeInSeconds;
        }
        catch {
            return null;
        }
    }

    getTokenIssuer(): string | null {
        const token = sessionStorage.getItem('token');

        if ((token === null) || (token === undefined)) {
            return null;
        }

        try {
            const decodedToken = jwtDecode<DecodedToken>(token);
            return decodedToken.iss ?? null;
        }
        catch {
            return null;
        }
    }

    getTokenType(): 'EntraExternalId' | 'EntraId' | 'Smarts' | 'Unknown' {
        let issuer = this.getTokenIssuer();

        if (!issuer) {
            return 'Unknown';
        }

        issuer = issuer.toLowerCase();

        if (issuer.includes('ciamlogin.com')) {
            return 'EntraExternalId';
        }

        if (issuer.includes('login.microsoftonline.com') || issuer.includes('sts.windows.net')) {
            return 'EntraId';
        }

        return 'Smarts';
    }

    getTokenEmail(): string | null {
        const token = sessionStorage.getItem('token');

        if ((token === null) || (token === undefined)) {
            return null;
        }

        try {
            const decodedToken = jwtDecode<DecodedToken>(token);
            return decodedToken.preferred_username
                ?? decodedToken.email
                ?? null;
        }
        catch {
            return null;
        }
    }
}
